import { AutocompleteFormComponent } from "components/Form/components/Autocomplete/AutocompleteFormComponent";
import { DateTimeFormComponent } from "components/Form/components/DateTime/DateTimeFormComponent";
import { NumberFormComponent } from "components/Form/components/Number/NumberFormComponent";
import { LazyResizeableCodeEditor as ResizeableCodeEditor } from "components/Form/lazyCodeEditorComponents";
import { getUpdatedData } from "components/Form/components/SchemaForm/SchemaFormService";
import { TextFormComponent } from "components/Form/components/Text/TextFormComponent";
import { IconCollection } from "components/Icon/IconCollection";
import { SchemaFieldType } from "config/config";
import { getLocalDateTime, getLocalTimeZoneISO } from "config/ts-utils";
import { CustomFilterValues } from "containers/AdvancedSearch/AdvancedSearch";
import { filterAdvancedResourceTypes } from "containers/AdvancedSearch/utils";
import { DeleteFieldButton } from "containers/Filter/FilterBuilder/components/FilterFragment";
import { buildReferenceValueOptions, isArrayOperator } from "containers/Filter/FilterBuilder/utils";
import { useCallback, useEffect, useState } from "react";
import { FieldInputProps, FieldMetaState } from "react-final-form";
import { isUuid } from "utils/utils";
import { parseYaml, serializeToPrettyYaml } from "utils/valueTransformers";

interface ValueFieldType {
    input: FieldInputProps<any, HTMLElement>;
    meta: FieldMetaState<any>;
    value: any;
    matchingSchema: SchemaFieldType;
    childPathValue: string;
    setData: (data: CustomFilterValues) => void;
    formData: CustomFilterValues;
    setErrorMessage: any;
    op: string;
    elementDataField: string;
}

/**
 * Coerces a raw text entry into a committed array item.
 * Returns `undefined` for empty / non-numeric-int input so the caller can drop it.
 */
const coerceArrayItem = (raw: string, isInt: boolean): string | number | undefined => {
    const trimmed = raw.trim();
    if (trimmed === "") return undefined;
    if (isInt) {
        const parsed = parseInt(trimmed, 10);
        return Number.isNaN(parsed) ? undefined : parsed;
    }
    return trimmed;
};

interface ArrayTextItemProps {
    initialText: string;
    isInt: boolean;
    isAddSlot: boolean;
    inputName: string;
    meta: FieldMetaState<any>;
    onCommit: (coerced: string | number | undefined) => void;
    onDelete?: () => void;
}

/**
 * A single slot of the multi-value tag input for free-text/integer `in`/`notin` filters.
 *
 * Each slot owns a LOCAL draft buffer and only commits to the (nested react-final-form)
 * filter value on Enter / comma / blur — never per keystroke. That keeps typing synchronous
 * so the caret stays put (the committed value round-trips through an initialValues-reinitialised
 * nested form that would otherwise re-set the DOM value one tick late and jump the caret — the
 * same class of issue solved for the scalar text input; see operator-task-fe-exists-filter-builder-fixes).
 */
const ArrayTextItem = ({
    initialText,
    isInt,
    isAddSlot,
    inputName,
    meta,
    onCommit,
    onDelete,
}: ArrayTextItemProps) => {
    const [draft, setDraft] = useState(initialText);
    // Resync when the committed item changes externally (item removed above this one,
    // field/operator switch, or the echo of our own commit — then a no-op).
    useEffect(() => {
        setDraft(initialText);
    }, [initialText]);

    const commit = () => {
        onCommit(coerceArrayItem(draft, isInt));
        if (isAddSlot) setDraft("");
    };

    return (
        <div className={isAddSlot ? "arrayValueEmpty" : "arrayValue"}>
            <TextFormComponent
                input={{
                    name: inputName,
                    value: draft,
                    onChange: (event: any) => setDraft(event.target.value),
                    onBlur: () => commit(),
                    onFocus: () => {},
                }}
                meta={meta}
                fieldProps={{}}
                fieldAttr={{
                    onKeyDown: (event: any) => {
                        if (event.key === "Enter" || event.key === ",") {
                            // Commit a tag instead of submitting the whole filter form.
                            event.preventDefault();
                            event.nativeEvent?.stopImmediatePropagation?.();
                            commit();
                        }
                    },
                }}
            />
            {!isAddSlot && onDelete && (
                <div className="deleteFieldButtonContainer">
                    <DeleteFieldButton onDelete={onDelete} />
                </div>
            )}
        </div>
    );
};

interface MultiValueTextFieldProps {
    value: any;
    isInt: boolean;
    input: FieldInputProps<any, HTMLElement>;
    meta: FieldMetaState<any>;
    formData: CustomFilterValues;
    childPathValue: string;
    setData: (data: CustomFilterValues) => void;
    elementDataField: string;
}

/**
 * Multi-value tag input for PLAIN free-text string and integer fields with the array
 * operators (`in` / `notin`). Mirrors the enum/foreign-key array container, but for typed
 * text/number entry: existing items are editable + removable, a trailing empty slot adds new
 * ones, values are de-duped, and integer items are coerced via parseInt (empties/NaN dropped).
 */
const MultiValueTextField = ({
    value,
    isInt,
    input,
    meta,
    formData,
    childPathValue,
    setData,
    elementDataField,
}: MultiValueTextFieldProps) => {
    const committed: (string | number)[] = Array.isArray(value) ? value : [];

    const commitArray = (next: (string | number)[]) => {
        setData(getUpdatedData(formData, childPathValue, next));
    };

    const replaceAt = (index: number, coerced: string | number | undefined) => {
        // Empty -> remove; duplicate of another item -> drop this slot; else replace in place.
        if (coerced === undefined || committed.some((v, i) => i !== index && v === coerced)) {
            commitArray(committed.filter((_, i) => i !== index));
            return;
        }
        if (committed[index] !== coerced) {
            const next = [...committed];
            next[index] = coerced;
            commitArray(next);
        }
    };

    const addItem = (coerced: string | number | undefined) => {
        if (coerced === undefined || committed.some((v) => v === coerced)) return;
        commitArray([...committed, coerced]);
    };

    const removeAt = (index: number) => {
        commitArray(committed.filter((_, i) => i !== index));
    };

    return (
        <div className="arrayValueContainer">
            {committed.map((item, index) => (
                <ArrayTextItem
                    key={index}
                    initialText={String(item)}
                    isInt={isInt}
                    isAddSlot={false}
                    inputName={`${input.name}-${elementDataField}-${index}`}
                    meta={meta}
                    onCommit={(coerced) => replaceAt(index, coerced)}
                    onDelete={() => removeAt(index)}
                />
            ))}
            <ArrayTextItem
                key="add"
                initialText=""
                isInt={isInt}
                isAddSlot
                inputName={`${input.name}-${elementDataField}-add`}
                meta={meta}
                onCommit={(coerced) => addItem(coerced)}
            />
        </div>
    );
};

/**
 * Component for rendering value input fields based on field type.
 * Handles different input types: text, number, datetime, boolean, enum, foreign key, array, and dict.
 * Includes validation logic for each field type and special handling for array operators.
 *
 * @param {ValueFieldType} props - Component props
 * @returns {JSX.Element | null} Rendered value field or null for boolean fields
 */
export const ValueField = ({
    matchingSchema,
    value,
    formData,
    childPathValue,
    setData,
    meta,
    input,
    setErrorMessage,
    op,
    elementDataField,
}: ValueFieldType) => {
    /**
     * Validates integer values against minimum/maximum constraints.
     *
     * @param {any} value - The value to validate
     */
    const validateInteger = (value) => {
        if (isNaN(value)) {
            setErrorMessage("Must be a number");
        } else if (Number(value) < matchingSchema.schema?.["minimum"]) {
            setErrorMessage(`Must be greater than ${matchingSchema.schema?.["minimum"]}`);
        } else if (Number(value) > matchingSchema.schema?.["maximum"]) {
            setErrorMessage(`Must be less than ${matchingSchema.schema?.["maximum"]}`);
        } else {
            setErrorMessage("");
        }
    };

    /**
     * Validates UUID format for fields with UUID schema.
     *
     * @param {any} value - The value to validate
     */
    const validateUuid = (value) => {
        if (matchingSchema.schema?.["format"] === "uuid" && !isUuid(value)) {
            setErrorMessage("Must be UUID format");
        } else {
            setErrorMessage("");
        }
    };

    /**
     * Validates text fields for required constraints.
     *
     * @param {any} value - The value to validate
     */
    const validateText = useCallback(
        (value) => {
            if (matchingSchema.nullable === false && value === "") {
                setErrorMessage("Required.");
            } else {
                setErrorMessage("");
            }
        },
        [matchingSchema.nullable, setErrorMessage]
    );

    /**
     * Validates autocomplete fields and triggers validation state updates.
     *
     * @param {any} value - The value to validate
     */
    const validateAutocomplete = (value) => {
        if (value) {
            // Trigger validation by setting different error messages for arrays and other value types
            if (isArrayOperator(op)) {
                setErrorMessage(undefined);
            } else {
                setErrorMessage(null);
            }
        }
    };

    /**
     * Validates date fields.
     *
     * @param {any} value - The value to validate
     */
    const validateDate = (value) => {
        if (value) {
            setErrorMessage("");
        }
    };

    // Local, synchronously-updated buffer for the free-text value inputs (str/int).
    // The committed value round-trips through a NESTED react-final-form (CustomFilterField),
    // whose `initialValues` reinitialise one commit LATE, so a plainly-controlled input
    // re-sets its DOM value a tick after each keystroke and the browser moves the caret to
    // the end (operator-task-fe-exists-filter-builder-fixes #4). Driving the input from local
    // state keeps typing synchronous so the caret stays put; we resync whenever the committed
    // value changes externally (field/op switch, or the echo of our own edit — then a no-op).
    const [localValue, setLocalValue] = useState(value);
    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    // Get enum schema if available
    const enumSchema = matchingSchema.schema?.["enum"];

    // Record-reference attribute (defect 2 of operator-task-advanced-search-…-uuid-value-picker).
    // A RECORD_REFERENCE / OBJECT_TEMPLATE_REFERENCE custom-object attribute is a uuid column;
    // objectTemplateFilterFields marks it schema.format="uuid" and carries the referenced
    // `record_type` (+ `object_template_id` for CO references). Presence of `record_type` on the
    // schema is the gate — a plain uuid string column without it stays a free-text field.
    const referenceRecordType = matchingSchema.schema?.["record_type"];
    const referenceObjectTemplateId = matchingSchema.schema?.["object_template_id"];
    const isReferenceField = typeof referenceRecordType === "string" && referenceRecordType !== "";

    /**
     * Generates options for autocomplete fields based on field type.
     * Handles foreign key relationships, enums, and resource types.
     *
     * @returns {Object} Options object for autocomplete component
     */
    const getOptions = useCallback(() => {
        if (matchingSchema.foreign_keys?.length) {
            // Handle foreign key relationships
            const resourceBody =
                matchingSchema.foreign_keys[0].table === "project" ||
                matchingSchema.foreign_keys[0].table === "bundle" ||
                matchingSchema.foreign_keys[0].table === "workspace" ||
                matchingSchema.foreign_keys[0].table === "user"
                    ? "icon"
                    : "";
            const iconReplacement =
                matchingSchema.foreign_keys[0].table === "project" ||
                matchingSchema.foreign_keys[0].table === "bundle"
                    ? IconCollection.folderTableOutline
                    : null;
            return {
                resource: matchingSchema.foreign_keys[0].table,
                resourceBody,
                iconReplacement,
            };
        } else if (isReferenceField) {
            // Reference attribute: a searchable record picker over the referenced type,
            // submitting the picked record's uuid. Mirrors DefaultValueEditor's reference
            // control (see buildReferenceValueOptions).
            const base = buildReferenceValueOptions(
                String(referenceRecordType),
                referenceObjectTemplateId
            );
            const iconReplacement =
                base.resource === "project" || base.resource === "bundle"
                    ? IconCollection.folderTableOutline
                    : null;
            return { ...base, iconReplacement };
        } else if (enumSchema) {
            // Handle enum fields
            const filteredEnumSchema =
                matchingSchema.name === "resource_type"
                    ? enumSchema.filter(filterAdvancedResourceTypes)
                    : enumSchema;
            return {
                list: filteredEnumSchema.map((item) => ({
                    value: item,
                    label: item.replaceAll("_", " "),
                })),
            };
        } else {
            return { list: [] };
        }
    }, [
        matchingSchema.foreign_keys,
        matchingSchema.name,
        enumSchema,
        isReferenceField,
        referenceRecordType,
        referenceObjectTemplateId,
    ]);

    // Handle Enter key press for form submission
    useEffect(() => {
        const keydownHandler = async (e) => {
            if (e.key === "Enter") {
                e.preventDefault();
                await validateText(value);
                await document.getElementById("filter-builder-form")?.dispatchEvent(
                    new Event("submit", {
                        bubbles: true, // whether the event will bubble up through the DOM or not
                        cancelable: true, // whether the event may be canceled or not
                    })
                );
            }
        };
        document.addEventListener("keydown", keydownHandler);
        return () => {
            document.removeEventListener("keydown", keydownHandler);
        };
    }, [validateText, value]);

    // Handle foreign key, enum, and record-reference fields with autocomplete
    if (matchingSchema.foreign_keys?.length || matchingSchema.schema?.["enum"] || isReferenceField) {
        const options = getOptions();

        // Handle array operators (in, notin) - render multiple autocomplete fields
        if (isArrayOperator(op)) {
            return (
                <div className="arrayValueContainer">
                    {/* Render existing array values */}
                    {Array.isArray(value) &&
                        value.map((fieldValue, index) => {
                            return (
                                <div className="arrayValue" key={index}>
                                    <AutocompleteFormComponent
                                        fieldProps={{
                                            label: "",
                                            options,
                                            customKey: `${input.name}-${elementDataField}`,
                                            fieldWidth: 240,
                                        }}
                                        input={{
                                            ...input,
                                            onChange: (changedValue) => {
                                                // Prevent duplicate values in array
                                                const isDuplicatedValue = value.find(
                                                    (v) => v === changedValue
                                                );
                                                if (!isDuplicatedValue) {
                                                    const newValue = [...value];
                                                    newValue.splice(index, 1, changedValue);
                                                    setData(
                                                        getUpdatedData(
                                                            formData,
                                                            childPathValue,
                                                            newValue
                                                        )
                                                    );
                                                    validateAutocomplete(changedValue);
                                                }
                                            },
                                            value: fieldValue,
                                        }}
                                        meta={meta}
                                        fieldAttr={{}}
                                    />
                                    {/* Delete button for individual array items */}
                                    <div className="deleteFieldButtonContainer">
                                        <DeleteFieldButton
                                            onDelete={() => {
                                                const newValue = value.filter(
                                                    (v) => v !== fieldValue
                                                );
                                                setData(
                                                    getUpdatedData(
                                                        formData,
                                                        childPathValue,
                                                        newValue
                                                    )
                                                );
                                            }}
                                        />
                                    </div>
                                </div>
                            );
                        })}

                    {/* Empty autocomplete field for adding new array items */}
                    <div className="arrayValueEmpty">
                        <AutocompleteFormComponent
                            fieldProps={{
                                label: "",
                                options,
                                customKey: `${input.name}-${elementDataField}`,
                                fieldWidth: 240,
                            }}
                            input={{
                                ...input,
                                onChange: (changedValue) => {
                                    const currentValue = [...(value ?? [])];
                                    // Prevent duplicate values
                                    const isDuplicatedValue = currentValue.find(
                                        (v) => v === changedValue
                                    );
                                    if (!isDuplicatedValue) {
                                        const newValue = [...currentValue, changedValue];
                                        setData(getUpdatedData(formData, childPathValue, newValue));
                                        validateAutocomplete(changedValue);
                                    }
                                },
                                value: null,
                            }}
                            meta={meta}
                            fieldAttr={{}}
                        />
                    </div>
                </div>
            );
        } else {
            // Single autocomplete field for non-array operators
            return (
                <AutocompleteFormComponent
                    fieldProps={{
                        label: "",
                        options,
                        customKey: `${input.name}-${elementDataField}`,
                        fieldWidth: 240,
                    }}
                    input={{
                        ...input,
                        onChange: (value) => {
                            setData(getUpdatedData(formData, childPathValue, value));
                            validateAutocomplete(value);
                        },
                        value,
                    }}
                    meta={meta}
                    fieldAttr={{}}
                    key={value}
                />
            );
        }
    }

    // Plain free-text string / integer fields with array operators (in/notin):
    // a multi-value tag input. (Foreign-key and enum strings are handled above with the
    // autocomplete array container; only unconstrained str/int reach here.)
    if (isArrayOperator(op) && (matchingSchema?.type === "str" || matchingSchema?.type === "int")) {
        return (
            <MultiValueTextField
                value={value}
                isInt={matchingSchema?.type === "int"}
                input={input}
                meta={meta}
                formData={formData}
                childPathValue={childPathValue}
                setData={setData}
                elementDataField={elementDataField}
            />
        );
    }

    // Handle different field types with appropriate input components
    switch (matchingSchema?.type) {
        case "bool":
            // Boolean fields are handled by the operator selection, no value input needed
            return null;

        case "str":
            // String input with UUID validation
            return (
                <TextFormComponent
                    input={{
                        ...input,
                        onChange: (event: any) => {
                            const { value } = event.target;
                            setLocalValue(value);
                            setData(getUpdatedData(formData, childPathValue, value));
                        },
                        onBlur: (event: any) => {
                            validateUuid(event?.target?.value);
                        },
                        value: localValue ?? "",
                    }}
                    meta={meta}
                    fieldProps={{}}
                    fieldAttr={{}}
                />
            );

        case "datetime":
            // DateTime input with timezone conversion
            return (
                <DateTimeFormComponent
                    input={{
                        ...input,
                        onChange: (value) => {
                            const valueToISO = getLocalTimeZoneISO(value);
                            setData(getUpdatedData(formData, childPathValue, valueToISO));
                        },
                        onBlur: (event: any) => {
                            validateDate(event?.target?.value);
                        },
                        value: value ? getLocalDateTime(value) : "",
                    }}
                    meta={meta}
                    fieldProps={{}}
                    fieldAttr={{}}
                />
            );

        case "int":
            // Integer input with range validation
            return (
                <NumberFormComponent
                    input={{
                        ...input,
                        onChange: (event: any) => {
                            if (event) {
                                const { value } = event.target;
                                setLocalValue(value);
                                setData(getUpdatedData(formData, childPathValue, parseInt(value)));
                            }
                        },
                        onBlur: (event: any) => {
                            validateInteger(event?.target?.value);
                        },
                        value: localValue ?? "",
                    }}
                    meta={meta}
                    fieldProps={{}}
                    fieldAttr={{}}
                />
            );

        case "dict":
            // YAML editor for dictionary/object fields
            return (
                <ResizeableCodeEditor
                    input={{
                        ...input,
                        value: serializeToPrettyYaml(value),
                        onChange: () => {},
                        onFocus: () => {},
                        onBlur: (event: any) => {
                            const { value } = event.target;
                            if (value === undefined) return;

                            try {
                                setData(getUpdatedData(formData, childPathValue, parseYaml(value)));
                            } catch (error) {
                                // TODO: improve error handling.
                                console.warn("failed to parse YAML", error);
                            }
                        },
                    }}
                    fieldAttr={{}}
                    meta={{}}
                    fieldProps={{
                        lang: "yaml",
                        initialHeight: 100,
                        minHeight: 100,
                    }}
                />
            );

        default:
            // No input component for unknown field types
            return null;
    }
};
